import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { FaCheck, FaChevronDown } from 'react-icons/fa';

/**
 * Reusable multi-select dropdown with checkboxes + an "All" (select-all) row.
 *
 * The menu is rendered in a body PORTAL (fixed position, high z-index) so it is never
 * clipped by an `overflow-hidden` ancestor (filter bars) nor hidden under other cards.
 *
 * Semantics:
 *   - `value` is an explicit array of selected option values.
 *   - "All" is checked when every option is selected (or when value is empty = "all").
 *     Toggling "All" ticks every item or clears them; ticking all items lights up "All".
 *   - Parent query convention: treat empty AND full as "no filter", e.g.
 *     `value.length && value.length < options.length ? { key: value } : {}`.
 */
const CheckboxMultiSelect = ({
  options = [],
  value = [],
  onChange,
  icon: Icon,
  allLabel = 'All',
  width = 'tw-w-56',
  menuWidth,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 224 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const w = menuWidth || Math.max(r.width, 224);
    let left = r.left;
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
    if (left < 8) left = 8;
    setPos({ top: r.bottom + 4, left, width: w });
  }, [menuWidth]);

  useLayoutEffect(() => { if (open) updatePos(); }, [open, updatePos]);

  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => updatePos();
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)
          && triggerRef.current && !triggerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updatePos]);

  const allValues = options.map((o) => o.value);
  const allChecked = options.length > 0 && value.length === options.length;

  const toggleAll = () => onChange(allChecked ? [] : allValues);
  const toggleItem = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const isChecked = (v) => value.includes(v);

  const label = (allChecked || value.length === 0)
    ? allLabel
    : value.length === 1
      ? (options.find((o) => o.value === value[0])?.label || '1 selected')
      : `${value.length} selected`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`tw-flex tw-items-center tw-gap-2 ${width} tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-solid tw-border-slate-300 tw-rounded-md tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 tw-cursor-pointer`}
      >
        {Icon && <Icon className="tw-text-blue-600 tw-flex-shrink-0" size={13} />}
        <span className="tw-flex-1 tw-text-left tw-truncate">{label}</span>
        <FaChevronDown size={11} className="tw-text-slate-400 tw-flex-shrink-0" />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 320, zIndex: 100100 }}
          className="tw-overflow-y-auto tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-rounded tw-shadow-xl tw-py-1"
        >
          <button
            type="button"
            onClick={toggleAll}
            className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-left tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-bg-slate-50"
          >
            <span className={`tw-w-4 tw-h-4 tw-rounded-sm tw-border tw-border-solid tw-flex tw-items-center tw-justify-center ${allChecked ? 'tw-bg-blue-600 tw-border-blue-600' : 'tw-border-slate-300'}`}>
              {allChecked && <FaCheck size={9} className="tw-text-white" />}
            </span>
            <span className="tw-font-medium">{allLabel}</span>
          </button>
          <div className="tw-my-1 tw-border-t tw-border-slate-100" />
          {options.map((o) => {
            const checked = isChecked(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggleItem(o.value)}
                className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-left tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-bg-slate-50"
              >
                <span className={`tw-w-4 tw-h-4 tw-rounded-sm tw-border tw-border-solid tw-flex tw-items-center tw-justify-center ${checked ? 'tw-bg-blue-600 tw-border-blue-600' : 'tw-border-slate-300'}`}>
                  {checked && <FaCheck size={9} className="tw-text-white" />}
                </span>
                {o.color && <span className="tw-w-2.5 tw-h-2.5 tw-rounded-xl tw-flex-shrink-0" style={{ backgroundColor: o.color }} />}
                <span className="tw-truncate">{o.label}</span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
};

CheckboxMultiSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.any, label: PropTypes.string, color: PropTypes.string })),
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  icon: PropTypes.elementType,
  allLabel: PropTypes.string,
  width: PropTypes.string,
  menuWidth: PropTypes.number,
};

export default CheckboxMultiSelect;
